interface FaderProps {
  label: string
  value: number
  min: number
  max: number
  step?: number
  onChange: (value: number) => void
  /** How the value reads in the display above the fader, e.g. "20%". */
  format?: (value: number) => string
  disabled?: boolean
  /** A second line under the label, e.g. "of 16 worked". */
  note?: string
  /** What the fader belongs to, for assistive technology - several channels each have a "Weight". */
  group?: string
}

const TICKS = [0, 1, 2, 3, 4]

/**
 * One fader of the desk: a lit readout, a vertical slider with a tick
 * scale, and its label. Keyboard arrows move it a step at a time.
 */
export function Fader({ label, value, min, max, step = 1, onChange, format = String, disabled = false, note, group }: FaderProps) {
  // A fader whose range has collapsed (0 of 0 shifts) has nowhere to go.
  const isStuck = max <= min

  return (
    <div className={`flex w-[58px] shrink-0 flex-col items-center gap-1.5 ${disabled ? 'opacity-60' : ''}`}>
      <div className="w-full rounded-sm bg-black/50 px-1 py-0.5 text-center font-mono text-[11px] font-bold tabular-nums text-coral shadow-inner">
        {format(value)}
      </div>
      <div className="relative flex h-[132px] items-stretch justify-center">
        <div aria-hidden="true" className="absolute inset-y-[6px] left-[-9px] flex flex-col justify-between">
          {TICKS.map((tick) => (
            <span key={tick} className="block h-px w-1.5 bg-white/30" />
          ))}
        </div>
        <input
          type="range"
          className="fader"
          aria-label={group ? `${group}: ${label}` : label}
          aria-valuetext={format(value)}
          min={min}
          max={isStuck ? min + 1 : max}
          step={step}
          value={value}
          disabled={disabled || isStuck}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      </div>
      <div className="text-center text-[9px] font-bold uppercase leading-tight tracking-wide text-white/80">{label}</div>
      {note && <div className="text-center text-[9px] leading-tight text-white/45">{note}</div>}
    </div>
  )
}
